import { Box } from '@mui/material';
import { useCallback, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { spaces } from '../../assets/styles/themes/tokens';
import services from '../../services';
import { NotificationContext } from '../../state/NotificationContextProvider';
import useScreenSize from '../../utils/useScreenSize';
import { password } from '../../validators';
import EditableSection from '../EditableSection';
import PasswordFieldsRenderer from '../PasswordFieldsRenderer';

export interface ChangePasswordPayload {
	currentPassword: string;
	newPassword: string;
	newPasswordRepeat: string;
}

interface ServerErrorMessage {
	field: keyof ChangePasswordPayload;
	message: string;
}

const ChangePasswordSection = () => {
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const { showNotification } = useContext(NotificationContext);

	const changePasswordFormValidator = yup.object({
		currentPassword: password({
			requiredMessage: 'Please enter your current password.',
		}),
		newPassword: password({ requiredMessage: 'Please enter a new password.' }),
		newPasswordRepeat: password().oneOf([yup.ref('newPassword')], 'Passwords must match'),
	});

	const { register, errors, getValues, triggerValidation, setError } =
		useForm<ChangePasswordPayload>({
			mode: 'onBlur',
			validationSchema: changePasswordFormValidator,
		});

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const onSaveChanges = useCallback(async ({ toggleEditMode }: { toggleEditMode: () => void }) => {
		const isValid = await triggerValidation(['currentPassword', 'newPassword', 'newPasswordRepeat']);

		if (!isValid) return;

		const values = getValues();

		try {
			await services.user.changePassword(
				values.currentPassword,
				values.newPassword,
				values.newPasswordRepeat
			);
			toggleEditMode();
			showNotification({
				message: t('All changes were saved successfully'),
				severity: 'success',
			});
		} catch (error: any) {
			showNotification({
				message: t('An error occurred while saving changes'),
				severity: 'error',
			});
			const serverErrorMessage = getServerErrorMessage(error.message);
			if (serverErrorMessage) {
				return setError(serverErrorMessage.field, 'manual', t(serverErrorMessage.message));
			}
		}
	}, []);

	const getServerErrorMessage = (errorMessage: string): ServerErrorMessage | undefined => {
		if (errorMessage.match(/Invalid username or password/)) {
			return {
				field: 'currentPassword',
				message: 'The password you entered is incorrect.',
			};
		}

		return;
	};

	return (
		<Box
			data-testid="change-password-section"
			sx={{
				display: 'grid',
				gridTemplateColumns: isXsScreen || isSmScreen ? '1fr' : '1fr 1fr',
				gap: spaces.md,
			}}
		>
			<EditableSection title={t('My Password')} onSaveChanges={onSaveChanges}>
				{({ isEditMode }) => (
					<PasswordFieldsRenderer isEditMode={isEditMode} register={register} errors={errors} />
				)}
			</EditableSection>
		</Box>
	);
};

export default ChangePasswordSection;
